import { FormEvent, useCallback, useEffect, useState } from 'react';
import {
  createGateLog,
  deleteGateLog,
  fetchGateLogs,
  updateGateLog,
} from '../../api/gateLog';
import ListPager from '../common/ListPager';

import { useCompanyStore } from '../../store/companyStore';
import {
  CompanyGateLog,
  GATE_LOG_TYPE_OPTIONS,
  GateLogType,
  formatGateLogTime,
  gateLogTypeLabel,
} from '../../types/gateLog';
import { notifyError, reportError } from '../../utils/notify';
import './LeaveRequestPanel.css';
import './GateLogPanel.css';

type View = 'list' | 'create' | 'edit';

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function formatDate(value?: string | null) {
  if (!value) return '-';
  return value.slice(0, 10);
}

function GateLogPanel() {
  const companyId = useCompanyStore((s) => s.companyId);
  const [view, setView] = useState<View>('list');
  const [rows, setRows] = useState<CompanyGateLog[]>([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState<'ALL' | GateLogType>('ALL');
  const [keyword, setKeyword] = useState('');

  const [logDate, setLogDate] = useState(todayStr());
  const [logTime, setLogTime] = useState('');
  const [logType, setLogType] = useState<GateLogType>('VISIT');
  const [title, setTitle] = useState('');
  const [partyName, setPartyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [trackingNo, setTrackingNo] = useState('');
  const [memo, setMemo] = useState('');

  const load = useCallback(
    async (pageNum = 0) => {
      try {
        setLoading(true);
        const result = await fetchGateLogs({
          type: typeFilter,
          keyword,
          page: pageNum,
        });
        setRows(result.content || []);
        setPage(result.page ?? pageNum);
        setTotalPages(result.totalPages ?? 0);
        setTotalElements(result.totalElements ?? 0);
        setError('');
      } catch {
        reportError(setError, '출입·수발신 목록을 불러오지 못했습니다.');
        setRows([]);
        setTotalPages(0);
        setTotalElements(0);
      } finally {
        setLoading(false);
      }
    },
    [typeFilter, keyword]
  );

  useEffect(() => {
    setView('list');
  }, [companyId, setView]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(0), 200);
    return () => window.clearTimeout(timer);
  }, [companyId, load]);

  const resetForm = (date?: string) => {
    setEditingId(null);
    setLogDate(date || todayStr());
    setLogTime('');
    setLogType('VISIT');
    setTitle('');
    setPartyName('');
    setContactName('');
    setContactPhone('');
    setTrackingNo('');
    setMemo('');
  };

  const openCreate = () => {
    resetForm();
    setError('');
    setView('create');
  };

  const openEdit = (row: CompanyGateLog) => {
    setEditingId(row.logId);
    setLogDate(row.logDate?.slice(0, 10) || todayStr());
    setLogTime(formatGateLogTime(row.logTime));
    setLogType((row.logType as GateLogType) || 'VISIT');
    setTitle(row.title || '');
    setPartyName(row.partyName || '');
    setContactName(row.contactName || '');
    setContactPhone(row.contactPhone || '');
    setTrackingNo(row.trackingNo || '');
    setMemo(row.memo || '');
    setError('');
    setView('edit');
  };

  const backToList = () => {
    setView('list');
    resetForm();
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      notifyError('내용(방문자명·요약)을 입력해 주세요.');
      return;
    }
    if (!logDate) {
      notifyError('날짜를 선택해 주세요.');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        logDate,
        logTime: logTime.trim() || undefined,
        logType,
        title: title.trim(),
        partyName: partyName.trim() || undefined,
        contactName: contactName.trim() || undefined,
        contactPhone: contactPhone.trim() || undefined,
        trackingNo: trackingNo.trim() || undefined,
        memo: memo.trim() || undefined,
      };
      if (editingId != null) {
        await updateGateLog(editingId, payload);
      } else {
        await createGateLog(payload);
      }
      backToList();
      await load(0);
    } catch (err) {
      notifyError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          '저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: CompanyGateLog) => {
    if (!window.confirm(`「${row.title}」 기록을 삭제할까요?`)) return;
    try {
      await deleteGateLog(row.logId);
      if (editingId === row.logId) backToList();
      await load(page);
    } catch {
      notifyError('삭제에 실패했습니다.');
    }
  };

  const isParcel = logType === 'PARCEL_IN' || logType === 'PARCEL_OUT';

  return (
    <main className="leave-panel gate-log-panel">
      <header className="leave-panel__head">
        <div>
          <p className="leave-panel__eyebrow">Gate Log</p>
          <h2>출입·수발신</h2>
          <p className="leave-panel__guide">
            방문·택배 수발신을 기록합니다. 대시보드 캘린더에도 표시됩니다. (재고와 무관)
          </p>
        </div>
        <div className="leave-panel__head-actions">
          {view !== 'list' ? (
            <button type="button" className="leave-panel__btn is-ghost" onClick={backToList}>
              목록으로
            </button>
          ) : (
            <button type="button" className="leave-panel__btn is-primary" onClick={openCreate}>
              기록 등록
            </button>
          )}
        </div>
      </header>

      {error ? <p className="leave-panel__error">{error}</p> : null}

      {view === 'list' ? (
        <div className="leave-panel__body">
          <div className="gate-log-panel__filters">
            <div className="gate-log-panel__type-tabs">
              {(
                [
                  { value: 'ALL', label: '전체' },
                  ...GATE_LOG_TYPE_OPTIONS,
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={typeFilter === opt.value ? 'is-active' : undefined}
                  onClick={() => setTypeFilter(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="방문자 / 업체 / 송장 / 메모 검색"
            />
          </div>

          {loading ? (
            <p className="leave-panel__hint">불러오는 중...</p>
          ) : rows.length === 0 ? (
            <div className="leave-panel__empty">
              <strong>기록이 없습니다</strong>
              <p>방문·택배 수발신을 등록해 보세요.</p>
              <button type="button" className="leave-panel__btn is-primary" onClick={openCreate}>
                기록 등록
              </button>
            </div>
          ) : (
            <div className="paged-list">
              <div className="paged-list__scroll">
                <table className="leave-panel__table">
                  <thead>
                    <tr>
                      <th style={{ width: 110 }}>날짜</th>
                      <th style={{ width: 72 }}>시간</th>
                      <th style={{ width: 100 }}>유형</th>
                      <th>내용</th>
                      <th style={{ width: 140 }}>소속·업체</th>
                      <th style={{ width: 120 }}>연락처</th>
                      <th style={{ width: 110 }}>담당·수령</th>
                      <th style={{ width: 120 }}>송장</th>
                      <th style={{ width: 120 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.logId} onDoubleClick={() => openEdit(row)}>
                        <td>{formatDate(row.logDate)}</td>
                        <td>{formatGateLogTime(row.logTime) || '-'}</td>
                        <td>
                          <span className={`gate-log-badge is-${String(row.logType).toLowerCase()}`}>
                            {row.logTypeLabel || gateLogTypeLabel(row.logType)}
                          </span>
                        </td>
                        <td>
                          <div className="gate-log-panel__title">{row.title}</div>
                          {row.memo ? <div className="gate-log-panel__memo">{row.memo}</div> : null}
                        </td>
                        <td>{row.partyName || '-'}</td>
                        <td>{row.contactPhone || '-'}</td>
                        <td>{row.contactName || '-'}</td>
                        <td>{row.trackingNo || '-'}</td>
                        <td>
                          <div className="gate-log-panel__row-actions">
                            <button type="button" className="is-ghost" onClick={() => openEdit(row)}>
                              수정
                            </button>
                            <button
                              type="button"
                              className="is-danger"
                              onClick={() => void handleDelete(row)}
                            >
                              삭제
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <ListPager
                page={page}
                totalPages={totalPages}
                totalElements={totalElements}
                loading={loading}
                onChange={(next) => void load(next)}
              />
            </div>
          )}
        </div>
      ) : (
        <form className="leave-panel__form gate-log-panel__form" onSubmit={(e) => void handleSubmit(e)}>
          <h3>{view === 'edit' ? '기록 수정' : '기록 등록'}</h3>

          <section className="gate-log-form__section">
            <div className="gate-log-form__section-head">
              <strong>일시·유형</strong>
              <span>언제, 어떤 기록인지</span>
            </div>
            <div className="gate-log-form__grid gate-log-form__grid--datetime">
              <label>
                <span>날짜 *</span>
                <input type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} required />
              </label>
              <label>
                <span>시간</span>
                <input type="time" value={logTime} onChange={(e) => setLogTime(e.target.value)} />
              </label>
            </div>
            <div className="gate-log-form__type-pills" role="group" aria-label="유형">
              {GATE_LOG_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={logType === opt.value ? `is-active is-${opt.value.toLowerCase()}` : undefined}
                  onClick={() => setLogType(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </section>

          <section className="gate-log-form__section">
            <div className="gate-log-form__section-head">
              <strong>{logType === 'VISIT' ? '방문 정보' : isParcel ? '택배 정보' : '기록 내용'}</strong>
              <span>
                {logType === 'VISIT'
                  ? '방문자와 담당자'
                  : isParcel
                    ? '업체·송장·담당'
                    : '요약과 연락처'}
              </span>
            </div>
            <div className="gate-log-form__grid">
              <label className="is-span-2">
                <span>{logType === 'VISIT' ? '방문자명 *' : '내용 요약 *'}</span>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={logType === 'VISIT' ? '홍길동' : '서류 / 부품 박스 등'}
                  required
                />
              </label>
              <label>
                <span>{logType === 'VISIT' ? '소속' : '업체·택배사'}</span>
                <input
                  value={partyName}
                  onChange={(e) => setPartyName(e.target.value)}
                  placeholder={logType === 'VISIT' ? '○○전자' : 'CJ대한통운'}
                />
              </label>
              <label>
                <span>{logType === 'VISIT' ? '방문자 연락처' : '상대 연락처'}</span>
                <input
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder="010-0000-0000"
                  inputMode="tel"
                />
              </label>
              <label>
                <span>{logType === 'VISIT' ? '담당자(우리)' : '수령·발송 담당'}</span>
                <input
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder="담당자명"
                />
              </label>
              {isParcel ? (
                <label>
                  <span>송장번호</span>
                  <input
                    value={trackingNo}
                    onChange={(e) => setTrackingNo(e.target.value)}
                    placeholder="송장번호"
                  />
                </label>
              ) : null}
            </div>
          </section>

          <section className="gate-log-form__section">
            <div className="gate-log-form__section-head">
              <strong>메모</strong>
              <span>목적·비고 (선택)</span>
            </div>
            <div className="gate-log-form__grid">
              <label className="is-span-2">
                <span className="visually-hidden">메모</span>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  placeholder="목적, 비고 등"
                  rows={3}
                />
              </label>
            </div>
          </section>

          <div className="leave-panel__head-actions gate-log-form__actions">
            <button type="button" className="leave-panel__btn is-ghost" onClick={backToList}>
              취소
            </button>
            <button type="submit" className="leave-panel__btn is-primary" disabled={saving}>
              {saving ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>
      )}
    </main>
  );
}

export default GateLogPanel;
